import { createContext } from 'react';
import type { Spell, Item, ClassFeature, Feat } from '../types';

interface ImportState {
  isLoading: boolean;
  error: string | null;
  successCount: number | null;
}

interface UnifiedImportState extends ImportState {
  detectedType: 'spells' | 'items' | 'classes' | 'feats' | null;
}

interface MultiTabImportCounts {
  spells: number;
  items: number;
  classes: number;
  feats: number;
}

interface MultiTabImportState {
  isLoading: boolean;
  error: string | null;
  successCounts: MultiTabImportCounts | null;
  warnings: string[];
}

export interface ImportContextValue {
  // Imported data
  customSpells: Spell[];
  customItems: Item[];
  customClasses: ClassFeature[];
  customFeats: Feat[];

  // Import state
  spellImportState: ImportState;
  itemImportState: ImportState;
  classImportState: ImportState;
  featImportState: ImportState;
  unifiedImportState: UnifiedImportState;
  multiTabImportState: MultiTabImportState;

  // Actions
  importSpells: (url: string) => Promise<void>;
  importItems: (url: string) => Promise<void>;
  importClasses: (url: string) => Promise<void>;
  importFeats: (url: string) => Promise<void>;
  importUnified: (url: string) => Promise<void>;
  importMultiTab: (url: string) => Promise<void>;
  importMultipleUrls: (urls: string[]) => Promise<void>;
  clearSpellImportStatus: () => void;
  clearItemImportStatus: () => void;
  clearClassImportStatus: () => void;
  clearFeatImportStatus: () => void;
  clearUnifiedImportStatus: () => void;
  clearMultiTabImportStatus: () => void;

  // Clear imported data (removes from state and localStorage)
  clearCustomSpells: () => void;
  clearCustomItems: () => void;
  clearCustomClasses: () => void;
  clearCustomFeats: () => void;

  // Template URLs
  spellTemplateUrl: string;
  itemTemplateUrl: string;
  classTemplateUrl: string;
  featTemplateUrl: string;
  combinedTemplateUrl: string;
}

export const ImportContext = createContext<ImportContextValue | null>(null);
